import React, { useState, useEffect, useMemo } from 'react';
import { useTemplate } from '../../contexts/TemplateContext';
import { TemplateCategory } from '../../types/template';
import TemplateGrid from './TemplateGrid';
import FilterBar from './FilterBar';
import LoadingState from './LoadingState';
import EmptyState from './EmptyState';
import { AccessibilityProvider, SkipLink, useAccessibilityContext } from './AccessibilityProvider';
import { useKeyboardNavigation } from '../../hooks/useAccessibility';
import { TooltipSystem, TooltipConfig } from './TooltipSystem';
import { useGlobalTemplates, useUserTemplates } from 'core';
import { useNavigate } from 'react-router-dom';
import { isDevMode } from 'lib/featureFlags';
import { ErrorState } from "components/ui/error-state";

export interface FilterOptions {
    category: TemplateCategory | 'all';
    search: string;
    sortBy: 'name' | 'date' | 'usage';
    sortOrder: 'asc' | 'desc';
    viewMode: 'grid' | 'list';
    showFavorites: boolean;
}

const TemplateGalleryContent: React.FC = () => {
    const navigate = useNavigate();
    const { state, actions: _actions } = useTemplate();
    const { templates } = state;
    const { announce } = useAccessibilityContext();

    const [filters, setFilters] = useState<FilterOptions>({
        category: 'all',
        search: '',
        sortBy: 'date',
        sortOrder: 'desc',
        viewMode: 'grid',
        showFavorites: false,
    });

    const [favorites, setFavorites] = useState<Set<string>>(new Set());

    // Fetch templates using React Query hooks
    const {
        data: globalTemplates,
        isLoading: loadingGlobalTemplate,
        error: globalError,
        refetch: refetchGlobal,
    } = useGlobalTemplates();
    const {
        data: userTemplates,
        isLoading: loadingUserTemplate,
        error: userError,
        refetch: refetchUser,
    } = useUserTemplates({ paginator: 0 });

    // Combined loading state
    const loading = loadingGlobalTemplate || loadingUserTemplate;

    // Combined error state
    const error = globalError || userError;

    // Get total template counts from API data
    const totalTemplates = (userTemplates?.items.length || 0) + (globalTemplates?.length || 0);

    useEffect(() => {
    }, [globalTemplates, userTemplates, totalTemplates, loading, filters.viewMode]);

    const filteredTemplates = useMemo(() => {
        let filtered = [...templates];

        // Apply category filter
        if (filters.category !== 'all') {
            filtered = filtered.filter(template => template.category === filters.category);
        }

        // Apply search filter
        if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            filtered = filtered.filter(template =>
                template.name.toLowerCase().includes(searchLower) ||
                template.description?.toLowerCase().includes(searchLower) ||
                template.metadata.tags.some(tag => tag.toLowerCase().includes(searchLower))
            );
        }

        // Apply favorites filter
        if (filters.showFavorites) {
            filtered = filtered.filter(template => favorites.has(template.id));
        }

        // Apply sorting
        filtered.sort((a, b) => {
            let comparison = 0;

            switch (filters.sortBy) {
                case 'name':
                    comparison = a.name.localeCompare(b.name);
                    break;
                case 'date':
                    comparison = a.metadata.updatedAt.getTime() - b.metadata.updatedAt.getTime();
                    break;
                case 'usage':
                    comparison = (a.performance?.usageCount || 0) - (b.performance?.usageCount || 0);
                    break;
            }

            return filters.sortOrder === 'desc' ? -comparison : comparison;
        });

        return filtered;
    }, [templates, filters, favorites]);

    useEffect(() => {
        if (totalTemplates > 0) {
            announce(`Showing ${totalTemplates} templates`);
        }
    }, [totalTemplates, announce]);

    // Keyboard navigation for template gallery
    useKeyboardNavigation(
        undefined,
        undefined,
        (direction) => {
            const templateElements = document.querySelectorAll('[data-template-card]');
            const currentIndex = Array.from(templateElements).findIndex(el => el === document.activeElement);

            if (currentIndex !== -1) {
                let nextIndex = currentIndex;

                if (direction === 'right' || direction === 'down') {
                    nextIndex = Math.min(currentIndex + 1, templateElements.length - 1);
                } else if (direction === 'left' || direction === 'up') {
                    nextIndex = Math.max(currentIndex - 1, 0);
                }

                if (nextIndex !== currentIndex) {
                    (templateElements[nextIndex] as HTMLElement).focus();
                }
            }
        }
    );

    const handleFilterChange = (newFilters: Partial<FilterOptions>) => {
        setFilters(prev => ({ ...prev, ...newFilters }));
    };

    const handleToggleFavorite = (templateId: string) => {
        setFavorites(prev => {
            const newFavorites = new Set(prev);
            if (newFavorites.has(templateId)) {
                newFavorites.delete(templateId);
            } else {
                newFavorites.add(templateId);
            }
            return newFavorites;
        });
    };

    const handleCreateNew = () => {
        navigate(`/templates/editor/new`);
    };

    // Tooltip configurations for help system
    const tooltipConfigs: TooltipConfig[] = [
        {
            id: 'create-button-help',
            target: '[aria-label="Create new template"]',
            title: 'Create Template',
            content: 'Start building a new email template with our drag-and-drop editor',
            position: 'bottom',
            trigger: 'hover',
            delay: 500,
        },
        {
            id: 'import-button-help',
            target: '[aria-label="Import template from file"]',
            title: 'Import Template',
            content: 'Upload an existing template file or import from another platform',
            position: 'bottom',
            trigger: 'hover',
            delay: 500,
        },
        {
            id: 'search-help',
            target: '#template-search',
            title: 'Smart Search',
            content: 'Search by template name, description, or tags. Results update as you type.',
            position: 'bottom',
            trigger: 'focus',
        },
        {
            id: 'favorites-help',
            target: '[aria-label*="favorite"]',
            title: 'Favorites',
            content: 'Mark templates as favorites for quick access. Toggle to show only your starred templates.',
            position: 'top',
            trigger: 'hover',
            delay: 700,
        },
    ];

    if (error) {
        // Was a bespoke red alert panel with no way to recover — the only
        // error surface in the app that offered no retry.
        return (
            <div className="p-4 sm:p-6">
                <ErrorState
                    title="Couldn't load your templates"
                    description="The request didn't complete. Your templates are safe — this is only the view."
                    error={error}
                    onRetry={() => {
                        refetchGlobal();
                        refetchUser();
                    }}
                />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Search and Filters Section - Wrapped in Card */}
            {
                isDevMode() &&
                <div className="bg-surface dark:bg-slate-800/80 rounded-xl shadow-sm border border-line-subtle dark:border-slate-700/60 p-6">
                    <FilterBar
                        filters={filters}
                        onFilterChange={handleFilterChange}
                        templateCount={totalTemplates}
                        totalCount={totalTemplates}
                    />
                </div>
            }

            {/* Content */}
            {loading ? (
                <LoadingState />
            ) : totalTemplates === 0 ? (
                <EmptyState
                    hasFilters={filters.search !== '' || filters.category !== 'all' || filters.showFavorites}
                    onCreateNew={handleCreateNew}
                    onClearFilters={() => setFilters({
                        category: 'all',
                        search: '',
                        sortBy: 'date',
                        sortOrder: 'desc',
                        viewMode: 'grid',
                        showFavorites: false,
                    })}
                />
            ) : (
                <TemplateGrid
                    templates={filteredTemplates}
                    viewMode={filters.viewMode}
                    favorites={favorites}
                    onToggleFavorite={handleToggleFavorite}
                    userTemplates={userTemplates}
                    globalTemplates={globalTemplates}
                    loadingUserTemplates={loadingUserTemplate}
                    loadingGlobalTemplates={loadingGlobalTemplate}
                />
            )}

            {/* Contextual tooltips */}
            <TooltipSystem
                tooltips={tooltipConfigs}
            />
        </div>
    );
};

const TemplateGallery: React.FC = () => {
    return (
        <AccessibilityProvider>
            <SkipLink href="#template-grid">Skip to template grid</SkipLink>
            <div aria-label="Template Gallery">
                <TemplateGalleryContent />
            </div>
        </AccessibilityProvider>
    );
};

export default TemplateGallery;